// Library discovery home (SAD-459 / LIB-01).
//
// Two scopes, not one list (C6-01): "Discover" is permitted reusable released
// items across any owner; "My items" is the viewer's own drafts/history.
// Both map to zou's SearchLibraryItems `scope` field ("" / "owner") — see
// ./Library/hooks.ts and services/zou/pkg/connecthandlers/library.go.
//
// Release/adopt/freeze/withdraw controls belong to SAD-462/461/464/465, not
// here — this stage is the discovery shell and the manual-create path only.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraInput, SpecraOverlayDialog, SpecraTabBar, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageBody, PageHero } from '../components/layout';
import { useDebounce } from '../hooks/useDebounce';
import { useCompanyProfiles } from './Delivery/Programs/referenceDataHooks';
import { formatTs, ITEM_TYPES, labelOf, parseDateInput, STATUSES } from './Library/constants';
import { useCreateLibraryItem, useSearchLibraryItems } from './Library/hooks';
const SEARCH_DEBOUNCE_MS = 300;
const LIBRARY_SUBTITLE = 'Reusable evidence, templates, and proof points — discover what is permitted to reuse, or manage your own drafts.';
const STATUS_STYLE = {
    draft: 'bg-amber-50 text-amber-800',
    released: 'bg-green-50 text-green-700',
    withdrawn: 'bg-gray-100 text-gray-600'
};
export default function Library() {
    const [scopeTab, setScopeTab] = useState('discover');
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
    const [itemType, setItemType] = useState('');
    const [customerAgencyName, setCustomerAgencyName] = useState('');
    const [providerCompanyProfileId, setProviderCompanyProfileId] = useState('');
    const [scopeContext, setScopeContext] = useState('');
    const [capabilityContext, setCapabilityContext] = useState('');
    const [status, setStatus] = useState('');
    const [updatedSince, setUpdatedSince] = useState('');
    const companies = useCompanyProfiles();
    const companyNames = useCompanyNameMap(companies.data);
    const filters = useMemo(()=>({
            query: debouncedQuery,
            itemType,
            customerAgencyName,
            providerCompanyProfileId,
            scopeContext,
            capabilityContext,
            // Discover results are always "released" already (libraryItemDenialReason
            // hides everything else from a non-owner), so a status filter is only
            // meaningful on My items.
            status: scopeTab === 'mine' ? status : '',
            updatedSince: parseDateInput(updatedSince)
        }), [
        debouncedQuery,
        itemType,
        customerAgencyName,
        providerCompanyProfileId,
        scopeContext,
        capabilityContext,
        status,
        scopeTab,
        updatedSince
    ]);
    const { data: items, isLoading } = useSearchLibraryItems(scopeTab === 'mine' ? 'owner' : '', filters);
    const create = useCreateLibraryItem();
    const [createOpen, setCreateOpen] = useState(false);
    const [formTitle, setFormTitle] = useState('');
    const [formSummary, setFormSummary] = useState('');
    const [formItemType, setFormItemType] = useState(ITEM_TYPES[0].value);
    const [formCustomerAgencyName, setFormCustomerAgencyName] = useState('');
    const [formProviderCompanyProfileId, setFormProviderCompanyProfileId] = useState('');
    const [formScopeContext, setFormScopeContext] = useState('');
    const [formCapabilityContext, setFormCapabilityContext] = useState('');
    const [formPreparedContent, setFormPreparedContent] = useState('');
    const resetForm = ()=>{
        setFormTitle('');
        setFormSummary('');
        setFormItemType(ITEM_TYPES[0].value);
        setFormCustomerAgencyName('');
        setFormProviderCompanyProfileId('');
        setFormScopeContext('');
        setFormCapabilityContext('');
        setFormPreparedContent('');
    };
    const submitCreate = ()=>{
        if (!formTitle.trim()) return;
        create.mutate({
            title: formTitle.trim(),
            summary: formSummary.trim(),
            itemType: formItemType,
            customerAgencyName: formCustomerAgencyName.trim(),
            providerCompanyProfileId: formProviderCompanyProfileId,
            scopeContext: formScopeContext.trim(),
            capabilityContext: formCapabilityContext.trim(),
            preparedContent: formPreparedContent
        }, {
            onSuccess: ()=>{
                setCreateOpen(false);
                resetForm();
            }
        });
    };
    const createDialog = /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
        open: createOpen,
        onOpenChange: setCreateOpen,
        title: "New Library item",
        description: "Every item is created as a draft — release, adoption, and freeze/withdraw happen elsewhere.",
        testId: Testids.library.createDialog,
        trigger: /*#__PURE__*/ _jsx(SpecraButton, {
            "data-testid": Testids.library.newItemButton,
            iconLeft: /*#__PURE__*/ _jsx(Plus, {
                className: "h-4 w-4"
            }),
            children: "New item"
        }),
        footerProps: {
            confirmLabel: create.isPending ? 'Creating…' : 'Create draft',
            onConfirm: submitCreate,
            confirmProps: {
                disabled: !formTitle.trim() || create.isPending,
                'data-testid': Testids.library.createSubmitButton
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4 py-2",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "library-create-title",
                            children: "Title"
                        }),
                        /*#__PURE__*/ _jsx(SpecraInput, {
                            id: "library-create-title",
                            value: formTitle,
                            onChange: (e)=>setFormTitle(e.target.value),
                            placeholder: "Past-performance narrative for Agency X O&M",
                            "data-testid": Testids.library.createTitleInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "library-create-summary",
                            children: "Summary"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "library-create-summary",
                            value: formSummary,
                            onChange: (e)=>setFormSummary(e.target.value),
                            placeholder: "Short description of what this reuses",
                            "data-testid": Testids.library.createSummaryInput
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-2 gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Item type"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: formItemType,
                                    onValueChange: setFormItemType,
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.library.createItemTypeSelect,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {})
                                        }),
                                        /*#__PURE__*/ _jsx(SelectContent, {
                                            children: ITEM_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "library-create-customer",
                                    children: "Customer/agency"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "library-create-customer",
                                    value: formCustomerAgencyName,
                                    onChange: (e)=>setFormCustomerAgencyName(e.target.value),
                                    "data-testid": Testids.library.createCustomerAgencyInput
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            children: "Provider Company"
                        }),
                        /*#__PURE__*/ _jsxs(Select, {
                            value: formProviderCompanyProfileId || undefined,
                            onValueChange: setFormProviderCompanyProfileId,
                            children: [
                                /*#__PURE__*/ _jsx(SelectTrigger, {
                                    "data-testid": Testids.library.createProviderCompanySelect,
                                    children: /*#__PURE__*/ _jsx(SelectValue, {
                                        placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                    })
                                }),
                                /*#__PURE__*/ _jsx(SelectContent, {
                                    children: (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                            value: c.id,
                                            children: c.name
                                        }, c.id))
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-2 gap-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "library-create-scope-context",
                                    children: "Scope context"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "library-create-scope-context",
                                    value: formScopeContext,
                                    onChange: (e)=>setFormScopeContext(e.target.value),
                                    "data-testid": Testids.library.createScopeContextInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    htmlFor: "library-create-capability-context",
                                    children: "Capability context"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    id: "library-create-capability-context",
                                    value: formCapabilityContext,
                                    onChange: (e)=>setFormCapabilityContext(e.target.value),
                                    "data-testid": Testids.library.createCapabilityContextInput
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(Label, {
                            htmlFor: "library-create-prepared-content",
                            children: "Prepared content"
                        }),
                        /*#__PURE__*/ _jsx(SpecraTextarea, {
                            id: "library-create-prepared-content",
                            value: formPreparedContent,
                            onChange: (e)=>setFormPreparedContent(e.target.value),
                            rows: 6,
                            placeholder: "The reusable text this item carries",
                            "data-testid": Testids.library.createPreparedContentInput
                        })
                    ]
                })
            ]
        })
    });
    const rows = items ?? [];
    return /*#__PURE__*/ _jsx(PageBody, {
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4 px-6 py-4",
            "data-testid": Testids.library.root,
            children: [
                /*#__PURE__*/ _jsx(PageHero, {
                    title: "Library",
                    subtitle: LIBRARY_SUBTITLE,
                    actions: createDialog
                }),
                /*#__PURE__*/ _jsx(SpecraTabBar, {
                    idBase: "library",
                    value: scopeTab,
                    onValueChange: setScopeTab,
                    options: [
                        {
                            value: 'discover',
                            label: 'Discover',
                            testId: Testids.library.scopeTabDiscover
                        },
                        {
                            value: 'mine',
                            label: 'My items',
                            testId: Testids.library.scopeTabMine
                        }
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[220px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Search"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: query,
                                    onChange: (e)=>setQuery(e.target.value),
                                    placeholder: "Title, summary, or content…",
                                    "data-testid": Testids.library.searchInput
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-44 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Item type"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: itemType || '__all__',
                                    onValueChange: (v)=>setItemType(v === '__all__' ? '' : v),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.library.filterItemType,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                                placeholder: "All types"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(SelectContent, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "__all__",
                                                    children: "All types"
                                                }),
                                                ITEM_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Customer/agency"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: customerAgencyName,
                                    onChange: (e)=>setCustomerAgencyName(e.target.value),
                                    "data-testid": Testids.library.filterCustomerAgency
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-48 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Provider Company"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: providerCompanyProfileId || '__all__',
                                    onValueChange: (v)=>setProviderCompanyProfileId(v === '__all__' ? '' : v),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.library.filterProviderCompany,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                                placeholder: "All companies"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(SelectContent, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "__all__",
                                                    children: "All companies"
                                                }),
                                                (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: c.id,
                                                        children: c.name
                                                    }, c.id))
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Scope context"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: scopeContext,
                                    onChange: (e)=>setScopeContext(e.target.value),
                                    "data-testid": Testids.library.filterScopeContext
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-[160px] flex-1 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Capability context"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    value: capabilityContext,
                                    onChange: (e)=>setCapabilityContext(e.target.value),
                                    "data-testid": Testids.library.filterCapabilityContext
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "w-40 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Updated since"
                                }),
                                /*#__PURE__*/ _jsx(SpecraInput, {
                                    type: "date",
                                    value: updatedSince,
                                    onChange: (e)=>setUpdatedSince(e.target.value),
                                    "data-testid": Testids.library.filterUpdatedSince
                                })
                            ]
                        }),
                        scopeTab === 'mine' && /*#__PURE__*/ _jsxs("div", {
                            className: "w-40 space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    className: "text-xs font-medium",
                                    children: "Status"
                                }),
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: status || '__all__',
                                    onValueChange: (v)=>setStatus(v === '__all__' ? '' : v),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            "data-testid": Testids.library.filterStatus,
                                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                                placeholder: "All statuses"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(SelectContent, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: "__all__",
                                                    children: "All statuses"
                                                }),
                                                STATUSES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: o.value,
                                                        children: o.label
                                                    }, o.value))
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                isLoading ? /*#__PURE__*/ _jsx("div", {
                    className: "text-sm text-muted-foreground",
                    children: "Searching Library…"
                }) : rows.length === 0 ? /*#__PURE__*/ _jsx(LibraryEmptyState, {
                    scope: scopeTab,
                    onCreate: ()=>setCreateOpen(true)
                }) : /*#__PURE__*/ _jsx("div", {
                    className: "grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3",
                    children: rows.map((item)=>/*#__PURE__*/ _jsx(LibraryItemCard, {
                            item: item,
                            providerName: companyNames.get(item.providerCompanyProfileId) ?? item.providerCompanyProfileId
                        }, item.id))
                })
            ]
        })
    });
}
function LibraryEmptyState({ scope, onCreate }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3 rounded-lg border border-dashed p-8 text-center",
        "data-testid": Testids.library.emptyState,
        children: [
            /*#__PURE__*/ _jsx("p", {
                className: "font-medium",
                children: scope === 'discover' ? 'No permitted reusable matches' : 'No Library items yet'
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mx-auto max-w-md text-sm text-muted-foreground",
                children: "A missing result here means there is no permitted reusable Library match for these filters — it is not proof that no relevant real-world experience exists."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                onClick: onCreate,
                "data-testid": Testids.library.emptyStateCreateButton,
                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                    className: "h-4 w-4"
                }),
                children: "Create a Library item"
            })
        ]
    });
}
function LibraryItemCard({ item, providerName }) {
    const context = [
        item.scopeContext,
        item.capabilityContext
    ].filter(Boolean).join(' · ');
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        "data-testid": Testids.library.resultCard,
        "data-item-id": item.id,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "pb-2",
                children: /*#__PURE__*/ _jsx(SpecraCardTitle, {
                    className: "text-base",
                    children: item.title
                })
            }),
            /*#__PURE__*/ _jsxs(SpecraCardContent, {
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-center gap-2 text-xs",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-muted-foreground",
                                "data-testid": Testids.library.resultType,
                                children: labelOf(ITEM_TYPES, item.itemType)
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center rounded-full px-2 py-0.5 ${STATUS_STYLE[item.status] ?? STATUS_STYLE.draft}`,
                                "data-testid": Testids.library.resultStatus,
                                children: labelOf(STATUSES, item.status)
                            })
                        ]
                    }),
                    item.summary && /*#__PURE__*/ _jsx("p", {
                        className: "line-clamp-2 text-sm text-muted-foreground",
                        children: item.summary
                    }),
                    /*#__PURE__*/ _jsxs("dl", {
                        className: "grid grid-cols-2 gap-x-3 gap-y-1 text-xs",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("dt", {
                                        className: "text-muted-foreground",
                                        children: "Customer/agency"
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        "data-testid": Testids.library.resultCustomer,
                                        children: item.customerAgencyName || '—'
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("dt", {
                                        className: "text-muted-foreground",
                                        children: "Provider"
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        "data-testid": Testids.library.resultProvider,
                                        children: providerName || '—'
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "col-span-2",
                                children: [
                                    /*#__PURE__*/ _jsx("dt", {
                                        className: "text-muted-foreground",
                                        children: "Scope / capability"
                                    }),
                                    /*#__PURE__*/ _jsx("dd", {
                                        "data-testid": Testids.library.resultContext,
                                        children: context || '—'
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-muted-foreground",
                        children: [
                            "Updated ",
                            formatTs(item.updatedAt)
                        ]
                    })
                ]
            })
        ]
    });
}
function useCompanyNameMap(profiles) {
    return useMemo(()=>{
        const m = new Map();
        for (const p of profiles ?? []){
            m.set(p.id, p.name);
        }
        return m;
    }, [
        profiles
    ]);
}
